import { isDefined } from "@clipboard-health/util-ts";
import type { TuFunction } from "@src/appV2/i18n/types";
import axios from "axios";
// eslint-disable-next-line no-restricted-imports -- following pattern from src/containers/postShifts/utils.ts
import { addDays, format, parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- following pattern from src/containers/postShifts/utils.ts
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";

import { type TimeSlot } from "../../Shift/types";
import { SHIFT_STATES, type ShiftCardData } from "./mapShiftToCard";

export const MINIMUM_SHIFT_DURATION_HOURS = 0.5;
export const MINIMUM_SHIFT_DURATION_MINUTES = MINIMUM_SHIFT_DURATION_HOURS * 60;
export const MAXIMUM_SHIFT_DURATION_HOURS = 17;
export const MAXIMUM_SHIFT_DURATION_MINUTES = MAXIMUM_SHIFT_DURATION_HOURS * 60;

/**
 * Determines the correct calendar dates for shift start and end times.
 * Handles overnight shifts where end time is earlier than start time.
 *
 * Uses formatInTimeZone to extract the time in the workplace timezone,
 * not the browser timezone.
 *
 * This follows the same pattern as src/containers/postShifts/utils.ts:getShiftDates
 */
function getShiftDates(
  day: Date,
  startTime: Date,
  endTime: Date,
  timezone: string
): { startDate: string; endDate: string } {
  const startDate = format(day, "yyyy-MM-dd");
  // Extract times in workplace timezone, not browser timezone
  const startTimeString = formatInTimeZone(startTime, timezone, "HH:mm");
  const endTimeString = formatInTimeZone(endTime, timezone, "HH:mm");

  // Detect overnight shift by comparing time strings (lexicographic comparison works for HH:mm)
  const isOvernight = endTimeString < startTimeString;

  // If overnight, end date is next calendar day
  const endDate = isOvernight ? format(addDays(parseISO(startDate), 1), "yyyy-MM-dd") : startDate;

  return { startDate, endDate };
}

/**
 * Converts a date string and time string to a UTC timestamp in the specified timezone.
 * Preserves wall-clock time during DST transitions.
 *
 * This follows the same pattern as src/containers/postShifts/utils.ts:buildTimestamp
 */
function buildTimestamp(dateString: string, timeString: string, timezone: string): Date {
  return zonedTimeToUtc(`${dateString}T${timeString}`, timezone);
}

/**
 * Calculates shift start and end timestamps in UTC, accounting for timezone and overnight shifts.
 *
 * Uses formatInTimeZone to extract times in the workplace timezone,
 * ensuring correct conversion regardless of browser timezone.
 *
 * This follows the same pattern as src/containers/postShifts/utils.ts:getShiftTimes
 */
export function getShiftTimestamps(
  day: Date,
  startTime: Date,
  endTime: Date,
  timezone: string
): { startAt: string; endAt: string } {
  const { startDate, endDate } = getShiftDates(day, startTime, endTime, timezone);
  // Extract times in workplace timezone, not browser timezone
  const startTimeString = formatInTimeZone(startTime, timezone, "HH:mm");
  const endTimeString = formatInTimeZone(endTime, timezone, "HH:mm");

  const startAt = buildTimestamp(startDate, startTimeString, timezone);
  const endAt = buildTimestamp(endDate, endTimeString, timezone);

  return { startAt: startAt.toISOString(), endAt: endAt.toISOString() };
}

export const OPTIMISTIC_ID_PREFIX = "optimistic-";

export function isOptimisticShiftId(shiftId: string | undefined): boolean {
  return isDefined(shiftId) && shiftId.startsWith(OPTIMISTIC_ID_PREFIX);
}

/**
 * DOM data-attributes used to locate daily-view columns and cards for
 * post-success scrolling. Shared between the components that render them and
 * `scrollToPostedShift`, which queries them — keep the producer and consumer on
 * the same constant so a rename can't silently break the scroll.
 */
export const DATA_ATTR_TIME_SLOT = "data-time-slot";
export const DATA_ATTR_SHIFT_ID = "data-shift-id";

/**
 * Creates an optimistic shift card for immediate UI feedback.
 * Generates a temporary ID that will be replaced when the server responds.
 */
export function createOptimisticShiftCard(params: {
  startAt: string;
  endAt: string;
  timeSlot: TimeSlot;
  qualification: string;
  optimisticId: string;
  orientationShiftType?: "single-shift";
}): ShiftCardData {
  return {
    shiftId: params.optimisticId,
    state: SHIFT_STATES.UNFILLED,
    agentReq: params.qualification,
    postedQualificationName: params.qualification,
    startTime: params.startAt,
    endTime: params.endAt,
    timeSlot: params.timeSlot,
    workerName: undefined,
    workerId: undefined,
    profileImageUrl: undefined,
    clockInTime: undefined,
    clockOutTime: undefined,
    hasUrgency: false,
    replacedShiftId: undefined,
    sentHomeInfo: undefined,
    extendBookabilityTo: undefined,
    computedArrival: undefined,
    pendingInvite: undefined,
    createdAt: new Date().toISOString(),
    orientationShiftType: params.orientationShiftType,
  };
}

/**
 * Gets an appropriate error message based on the error type.
 */
export function getShiftPostingErrorMessage(error: unknown, tu: TuFunction = identityTu): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;

    if (status === 403) {
      return tu("You don't have permission to post shifts. Please contact your administrator.");
    }

    if (status === 400) {
      return tu("Invalid shift data. Please check your inputs and try again.");
    }

    if (status === 409) {
      return tu("A conflict occurred while posting the shift. Please try again.");
    }

    if (status !== undefined && status >= 500) {
      return tu("Server error. Please try again later.");
    }
  }

  return tu("Failed to post shift. Please try again.");
}

function identityTu(defaultText: string): string {
  return defaultText;
}
